import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FormControl, InputLabel, IconButton } from '@mui/material'
import { useEditor, useEditorState, EditorContent } from '@tiptap/react'
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Controller } from 'react-hook-form'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { FormatListBulleted, FormatListNumbered, Link as LinkIcon } from '@mui/icons-material'
import { FormatBoldIcon } from 'assets/icons'
import { FormatItalicIcon } from 'assets/icons/format-italic'
import { FormatUnderlineIcon } from 'assets/icons/format-underline'
import { CharacterCount as tiptapCharacterCount } from '@tiptap/extension-character-count'

interface Props {
  name: string
  control: any
  label: JSX.Element | string
  disabled?: boolean
  required?: boolean
  defaultValue?: string
  placeholder?: string
  readMoreAtCharacter?: number
  maxCharactersSoftLimit?: number
  minHeight?: string
  maxHeight?: string
}

export default function RichTextEditor({
  name,
  control,
  label,
  disabled = false,
  required = false,
  defaultValue = '',
  placeholder,
  readMoreAtCharacter,
  maxCharactersSoftLimit,
  minHeight,
  maxHeight,
}: Props): JSX.Element {
  // Wrap the editor in Controller to integrate with react-hook-form
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <RichTextEditorContent
          value={value}
          onChange={onChange}
          label={label}
          disabled={disabled}
          required={required}
          readMoreAtCharacter={readMoreAtCharacter}
          maxCharactersSoftLimit={maxCharactersSoftLimit}
          minHeight={minHeight}
          maxHeight={maxHeight}
          placeholder={placeholder}
        />
      )}
    />
  )
}

interface EditorContentProps {
  value: string
  onChange: (value: string) => void
  label: JSX.Element | string
  disabled: boolean
  required: boolean
  readMoreAtCharacter?: number
  maxCharactersSoftLimit?: number
  minHeight?: string
  maxHeight?: string
  placeholder?: string
}

function RichTextEditorContent({
  value,
  onChange,
  label,
  disabled,
  required,
  readMoreAtCharacter,
  maxCharactersSoftLimit,
  minHeight,
  maxHeight,
  placeholder,
}: EditorContentProps): JSX.Element {
  const [characterCount, setCharacterCount] = useState(0)

  // Create a custom extension for the read more marker
  const ReadMoreMarker = Extension.create({
    name: 'readMoreMarker',

    addProseMirrorPlugins() {
      const readMoreLimit = readMoreAtCharacter

      return [
        new Plugin({
          key: new PluginKey('readMoreMarker'),
          props: {
            decorations(state) {
              if (!readMoreLimit) {
                return DecorationSet.empty
              }

              const decorations: Decoration[] = []
              let charCount = 0
              let markerAdded = false

              state.doc.descendants((node, pos) => {
                if (markerAdded) {
                  return false
                }

                if (node.isText && node.text) {
                  const textLength = node.text.length

                  if (charCount + textLength >= readMoreLimit) {
                    // Calculate position within this text node
                    const offsetInNode = readMoreLimit - charCount
                    const markerPos = pos + offsetInNode

                    // Create a widget decoration at this position
                    decorations.push(
                      Decoration.widget(
                        markerPos,
                        () => {
                          const marker = document.createElement('span')
                          marker.className = 'read-more-marker'
                          marker.textContent = '✂'
                          marker.title = `Read more limit (${readMoreLimit} characters)`
                          return marker
                        },
                        {
                          side: 0,
                        },
                      ),
                    )
                    markerAdded = true
                    return false
                  }

                  charCount += textLength
                }
                return undefined
              })

              return DecorationSet.create(state.doc, decorations)
            },
          },
        }),
      ]
    },
  })

  const normalizeStyledUnicode = (text: string): string => text.normalize('NFKC')

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Underline,
      BulletList,
      OrderedList,
      ListItem,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      ReadMoreMarker,
      Placeholder.configure({
        placeholder: placeholder,
      }),
      tiptapCharacterCount.configure({
        limit: maxCharactersSoftLimit,
      }),
    ],
    content: normalizeStyledUnicode(convertFromTextMeshProTags(convertCustomFormatToLinks(value || ''))),
    editorProps: {
      transformPastedText: (text) => normalizeStyledUnicode(text),
      transformPastedHTML: (html) => normalizeStyledUnicode(html),
    },
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      // Convert to TextMeshPro format and strip paragraphs
      const customHtml = stripParagraphTags(convertToTextMeshProTags(convertLinksToCustomFormat(html)))
      onChange(customHtml)
      // Update character count
      setCharacterCount(editor.state.doc.textContent.length)
    },
  })

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive('bold') ?? false,
      isItalic: editor?.isActive('italic') ?? false,
      isUnderline: editor?.isActive('underline') ?? false,
      isBulletList: editor?.isActive('bulletList') ?? false,
      isOrderedList: editor?.isActive('orderedList') ?? false,
      isLink: editor?.isActive('link') ?? false,
      canBold: editor?.can().chain().focus().toggleBold().run() ?? false,
      canItalic: editor?.can().chain().focus().toggleItalic().run() ?? false,
      canUnderline: editor?.can().chain().focus().toggleUnderline().run() ?? false,
    }),
  })

  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = stripParagraphTags(convertToTextMeshProTags(convertLinksToCustomFormat(editor.getHTML())))
      const convertedValue = wrapInParagraphs(convertFromTextMeshProTags(convertCustomFormatToLinks(value)))

      // Only update if content is actually different
      if (currentContent !== value && editor.getHTML() !== convertedValue) {
        editor.commands.setContent(convertedValue, { emitUpdate: false })
      }
    }
  }, [value, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  const setLink = () => {
    if (!editor) {
      return
    }

    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('Enter link URL:', previousUrl)

    if (url === null) {
      return
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  if (!editor) {
    return <></>
  }

  return (
    <InputContainer margin="normal" fullWidth required={required}>
      <FieldLabel disabled={disabled}>{label}</FieldLabel>
      <EditorWrapper disabled={disabled} $minHeight={minHeight} $maxHeight={maxHeight}>
        <Toolbar>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!toolbarState.canBold}
            className={toolbarState.isBold ? 'is-active' : ''}
            title="Bold"
          >
            <FormatBoldIcon />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!toolbarState.canItalic}
            className={toolbarState.isItalic ? 'is-active' : ''}
            title="Italic"
          >
            <FormatItalicIcon />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!toolbarState.canUnderline}
            className={toolbarState.isUnderline ? 'is-active' : ''}
            title="Underline"
          >
            <FormatUnderlineIcon />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={toolbarState.isBulletList ? 'is-active' : ''}
            title="Bullet List"
          >
            <FormatListBulleted fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={toolbarState.isOrderedList ? 'is-active' : ''}
            title="Numbered List"
          >
            <FormatListNumbered fontSize="small" />
          </ToolbarButton>
          <Divider />
          <ToolbarButton onClick={setLink} className={toolbarState.isLink ? 'is-active' : ''} title="Link">
            <LinkIcon fontSize="small" />
          </ToolbarButton>
        </Toolbar>
        <EditorContent editor={editor} />
      </EditorWrapper>
      <CharacterCount
        disabled={disabled}
        $exceededLimit={maxCharactersSoftLimit !== undefined && characterCount > maxCharactersSoftLimit}
      >
        {maxCharactersSoftLimit !== undefined ? `${characterCount} / ${maxCharactersSoftLimit}` : `${characterCount}`}
      </CharacterCount>
    </InputContainer>
  )
}

// Helper functions moved outside component to avoid recreation
const convertLinksToCustomFormat = (html: string): string => {
  return html
    ? html.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, text) => {
        return `<link="${href}">${text}</link>`
      })
    : ''
}

const convertCustomFormatToLinks = (html: string): string => {
  return html
    ? html.replace(/<link="([^"]*)">(.*?)<\/link>/gi, (match, id, text) => {
        return `<a href="${id}">${text}</a>`
      })
    : ''
}

const convertToTextMeshProTags = (html: string): string => {
  // Convert standard HTML tags to TextMeshPro compatible tags
  // Also convert <br> to newlines
  return html
    .replace(/<strong>/gi, '<b>')
    .replace(/<\/strong>/gi, '</b>')
    .replace(/<em>/gi, '<i>')
    .replace(/<\/em>/gi, '</i>')
    .replace(/<br\s*\/?>/gi, '\n')
}

const convertFromTextMeshProTags = (html: string): string => {
  // Convert TextMeshPro tags back to standard HTML for editor
  // Also convert newlines to <br>
  return html
    .replace(/<b>/gi, '<strong>')
    .replace(/<\/b>/gi, '</strong>')
    .replace(/<i>/gi, '<em>')
    .replace(/<\/i>/gi, '</em>')
    .replace(/\n/g, '<br>')
}

const stripParagraphTags = (html: string): string => {
  // Remove opening and closing <p> tags but keep the content with newlines
  return html
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '\n')
    .replace(/\n+$/g, '') // Remove trailing newlines
    .trim()
}

const wrapInParagraphs = (html: string): string => {
  // If content doesn't have block-level elements, wrap lines in paragraphs for the editor
  if (!html.includes('<p>') && !html.includes('<ul>') && !html.includes('<ol>')) {
    const lines = html.split(/\n|<br\s*\/?>/gi).filter((line) => line.trim())
    if (lines.length === 0) {
      return '<p></p>'
    }
    return lines.map((line) => `<p>${line}</p>`).join('')
  }
  return html
}

const InputContainer = styled(FormControl)`
  margin: 20px 0;
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const EditorWrapper = styled.div<{ disabled: boolean; $minHeight?: string; $maxHeight?: string }>`
  border: 2px solid #e4e4e7;
  border-radius: 8px;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;

  .tiptap-wrapper {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  .tiptap {
    flex: 1;
    padding: 20px;
    min-height: ${(props) => props.$minHeight || 'none'};
    max-height: ${(props) => props.$maxHeight || 'none'};
    overflow-y: ${(props) => (props.$maxHeight ? 'auto' : 'visible')};
    outline: none;

    &:focus {
      outline: none;
    }

    p {
      margin: 0 0 1em 0;
    }

    ul,
    ol {
      padding-left: 1.5em;
      margin: 0 0 1em 0;
    }

    ul {
      list-style: disc;
    }

    ol {
      list-style: decimal;
    }

    a {
      color: ${(props) => props.theme.palette.primary.main};
      text-decoration: underline;
      cursor: pointer;
    }

    strong {
      font-weight: bold;
    }

    em {
      font-style: italic;
    }

    u {
      text-decoration: underline;
    }

    .read-more-marker {
      display: inline-block;
      color: white;
      font-size: 18px;
      font-weight: bold;
      margin: 0 2px;
      cursor: help;
      vertical-align: middle;
      background-color: ${(props) => props.theme.palette.primary.main};
      padding: 0 4px;
      border-radius: 3px;
    }
  }

  /* Placeholder css */
  .tiptap p.is-editor-empty:first-child::before {
    color: ${(props) => props.theme.customPalette.mediumGray};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
`

const Toolbar = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 2px solid;
  align-self: stretch;
  border-color: #e4e4e7;
`

const ToolbarButton = styled(IconButton)`
  &.is-active {
    background-color: #e4e4e7;
    border-radius: 8px;
    color: white;

    &:hover {
      background-color: ${(props) => props.theme.palette.primary.dark};
      border-radius: 8px;
    }
  }

  &:disabled {
    opacity: 0.3;
  }
`

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #e4e4e7;
  height: 20px;
  align-self: center;
  margin: 0 4px;
`

const CharacterCount = styled.div<{ disabled?: boolean; $exceededLimit: boolean }>`
  padding-top: 8px;
  text-align: right;
  font-size: 12px;
  color: ${(props) => (props.$exceededLimit ? props.theme.palette.error.main : props.theme.customPalette.darkGray)};
  font-weight: ${(props) => (props.$exceededLimit ? 'bold' : 'normal')};
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
`
